/* eslint-disable */
import React, { useState, useCallback, useEffect } from 'react';
import './BrewedShop.css';

import { BREWED_DATA } from './Data';
import { Header, Footer, Toast, SearchModal, Icon } from './Primitives';
import { HomePage, PLPPage, PDPPage, BlogPage, ContactPage, FAQPage } from './Pages';
import { CartPage, CheckoutPage, ConfirmationPage, WishlistPage, AccountPage, AuthPage } from './Flow';
import Quiz from './Quiz';
import Admin from './Admin';

const PROMOS = {
  BREWED10: 0.10,
  SPRING26: 0.15,
};

const CART_STORAGE_KEY = 'brewedshop_cart';

function loadStoredCart() {
  try {
    const saved = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Private browsing, storage disabled, or corrupted data — just start empty
    // rather than breaking the page.
    return [];
  }
}

export default function BrewedShop() {
  const [page, setPage] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return hash || 'home';
  });
  const [pageOpts, setPageOpts] = useState({});
  const [pdpProduct, setPdpProduct] = useState(null);

  const [cart, setCart] = useState(loadStoredCart);
  const [wishlist, setWishlist] = useState([]);
  const [user, setUser] = useState(null);
  const [order, setOrder] = useState(null);
  const [promoState, setPromoState] = useState({ applied: false, code: '', discount: 0, msg: '' });

  const [toast, setToast] = useState({ msg: '', shown: false });
  const [searchOpen, setSearchOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch {
      // Storage unavailable — cart just won't persist this session, not worth breaking the page over.
    }
  }, [cart]);

  const showToast = useCallback((msg) => {
    setToast({ msg, shown: true });
    setTimeout(() => setToast(t => ({ ...t, shown: false })), 2400);
  }, []);

  const onNav = useCallback((target, opts = {}) => {
    setPage(target);
    setPageOpts(opts);
    window.location.hash = target;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const onOpen = useCallback((product) => {
    setPdpProduct(product);
    setPage('pdp');
    window.location.hash = 'pdp';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const addToCart = useCallback((product, qty = 1) => {
    setCart(prev => {
      const idx = prev.findIndex(l =>
        l.id === product.id &&
        l.size === product.size &&
        l.grind === product.grind &&
        l.color === product.color
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [...prev, { ...product, qty }];
    });
    showToast(`${product.name} added to cart`);
  }, [showToast]);

  const updateQty = useCallback((idx, qty) => {
    setCart(prev => { const next = [...prev]; next[idx] = { ...next[idx], qty }; return next; });
  }, []);

  const removeFromCart = useCallback((idx) => {
    setCart(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const toggleWish = useCallback((id) => {
    setWishlist(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const onLogin = useCallback((u) => {
    setUser(u);
    onNav('home');
  }, [onNav]);

  const applyPromo = useCallback((code) => {
    const discount = PROMOS[code.toUpperCase()];
    if (discount) {
      setPromoState({ applied: true, code: code.toUpperCase(), discount, msg: `${code.toUpperCase()} applied — ${(discount * 100).toFixed(0)}% off` });
    } else {
      setPromoState(p => ({ ...p, applied: false, msg: 'Code not recognized' }));
    }
  }, []);

  const completeOrder = useCallback((orderData) => {
    setOrder(orderData);
    setCart([]);
    setPromoState({ applied: false, code: '', discount: 0, msg: '' });
    onNav('confirmation');
  }, [onNav]);

  const cartCount = cart.reduce((s, l) => s + l.qty, 0);
  const wishCount = wishlist.length;

  const sharedProps = { data: BREWED_DATA, onNav, onOpen, addToCart, wishlist, toggleWish };

  const renderPage = () => {
    switch (page) {
      case 'home':
        return <HomePage {...sharedProps} onOpenQuiz={() => setQuizOpen(true)} />;
      case 'shop':
        return <PLPPage {...sharedProps} initialType={pageOpts.type || null} />;
      case 'pdp':
        return pdpProduct
          ? <PDPPage p={pdpProduct} {...sharedProps} />
          : <HomePage {...sharedProps} onOpenQuiz={() => setQuizOpen(true)} />;
      case 'blog':
        return <BlogPage data={BREWED_DATA} />;
      case 'contact':
        return <ContactPage onNav={onNav} />;
      case 'faq':
        return <FAQPage data={BREWED_DATA} onNav={onNav} />;
      case 'cart':
        return <CartPage cart={cart} updateQty={updateQty} removeFromCart={removeFromCart} onNav={onNav} applyPromo={applyPromo} promoState={promoState} />;
      case 'checkout':
        return <CheckoutPage cart={cart} promoState={promoState} onNav={onNav} completeOrder={completeOrder} user={user} />;
      case 'confirmation':
        return <ConfirmationPage order={order} onNav={onNav} />;
      case 'wishlist':
        return <WishlistPage data={BREWED_DATA} wishlist={wishlist} onOpen={onOpen} addToCart={addToCart} toggleWish={toggleWish} onNav={onNav} />;
      case 'account':
        return user
          ? <AccountPage user={user} onNav={onNav} />
          : <AuthPage onLogin={onLogin} onNav={onNav} />;
      case 'auth':
        return <AuthPage onLogin={onLogin} onNav={onNav} />;
      default:
        return <HomePage {...sharedProps} onOpenQuiz={() => setQuizOpen(true)} />;
    }
  };

  return (
    <div className="brewed">
      <Header
        page={page}
        onNav={onNav}
        cartCount={cartCount}
        wishCount={wishCount}
        user={user}
        onLogin={() => onNav('auth')}
        onSearch={() => setSearchOpen(true)}
      />

      <main>{renderPage()}</main>

      <Footer onNav={onNav} />

      <Toast msg={toast.msg} shown={toast.shown} />

      {searchOpen && (
        <SearchModal
          data={BREWED_DATA}
          onClose={() => setSearchOpen(false)}
          onOpen={(p) => { onOpen(p); setSearchOpen(false); }}
        />
      )}

      {quizOpen && (
        <Quiz
          data={BREWED_DATA}
          onClose={() => setQuizOpen(false)}
          onOpen={onOpen}
          addToCart={addToCart}
        />
      )}

      {adminOpen && (
        <Admin
          data={BREWED_DATA}
          onClose={() => setAdminOpen(false)}
        />
      )}

      {/* Demo-only affordance for reviewers — no equivalent exists on a real
          storefront, so it stays as a low-visibility floating trigger rather
          than a top-of-page bar. Reveals its label on hover/focus. */}
      <button className="bw-demo-trigger" onClick={() => setAdminOpen(true)} aria-label="Open admin panel (demo)">
        <Icon name="sliders" className="icon icon--sm" />
        <span className="bw-demo-trigger-label">Admin panel</span>
      </button>
    </div>
  );
}